<template>
  <div class="font-medium ml-8" :class="{ 'flex flex-col items-center': !isModal }">
    <div class="text-lg">{{ t('authentication.signup.heading') }}</div>
    <div class="text-base">{{ t('authentication.signup.subheading') }}</div>

    <div class="mt-5 font-normal flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <SfIconPerson class="text-primary-500" />
        <div>{{ t('authentication.signup.benefits.saveAddresses') }}</div>
      </div>
      <div class="flex items-center gap-2">
        <SfIconLocalShipping class="text-primary-500" />
        <div>{{ t('authentication.signup.benefits.orderTracking') }}</div>
      </div>
      <div class="flex items-center gap-2">
        <SfIconFavorite class="text-primary-500" />
        <div>{{ t('authentication.signup.benefits.wishlist') }}</div>
      </div>
      <div class="flex items-center gap-2">
        <SfIconSchedule class="text-primary-500" />
        <div>{{ t('authentication.signup.benefits.orderHistory') }}</div>
      </div>
    </div>
  </div>

  <div class="flex items-center justify-center my-1">
    <form class="flex flex-col gap-4 p-2 @md:p-6 rounded-md w-full @md:w-[400px]" @submit.prevent="onSubmit">
      <label>
        <UiFormLabel>{{ t('form.emailLabel') }} {{ t('form.required') }}</UiFormLabel>
        <SfInput
          v-model="email"
          v-bind="emailAttributes"
          :invalid="!!errors['register.email']"
          name="customerEmail"
          type="email"
          autocomplete="email"
        />
        <ErrorMessage as="span" name="register.email" class="flex text-negative-700 text-sm mt-2" />
      </label>

      <label>
        <UiFormLabel>{{ t('form.passwordLabel') }} {{ t('form.required') }}</UiFormLabel>
        <UiFormPasswordInput
          v-model="password"
          :title="t('authentication.signup.passwordValidation.invalidPassword')"
          name="password"
          autocomplete="current-password"
          v-bind="passwordAttributes"
          :invalid="!!errors['register.password']"
          @input="stripSpaces('password')"
        />
        <ErrorMessage as="span" name="register.password" class="flex text-negative-700 text-sm mt-2" />
      </label>

      <label>
        <UiFormLabel>{{ t('form.repeatPasswordLabel') }} {{ t('form.required') }}</UiFormLabel>
        <UiFormPasswordInput
          v-model="repeatPassword"
          :title="t('authentication.signup.passwordValidation.invalidPassword')"
          name="password"
          autocomplete="current-password"
          v-bind="repeatPasswordAttributes"
          :invalid="!!errors['register.repeatPassword']"
          @input="stripSpaces('repeatPassword')"
        />
        <ErrorMessage as="span" name="register.repeatPassword" class="flex text-negative-700 text-sm mt-2" />
      </label>

      <div class="text-xs">
        <div class="flex items-center" :class="{ 'text-green-600': passwordValidationLength }">
          <SfIconCheck v-if="passwordValidationLength" size="sm" />
          <SfIconClose v-else size="sm" />
          <span class="ml-1">
            {{
              t('authentication.signup.passwordValidation.characters', {
                min: passwordMinLength,
                max: passwordMaxLength,
              })
            }}
          </span>
        </div>
        <div class="flex items-center" :class="{ 'text-green-600': passwordValidationOneDigit }">
          <SfIconCheck v-if="passwordValidationOneDigit" size="sm" />
          <SfIconClose v-else size="sm" />
          <span class="ml-1">{{ t('authentication.signup.passwordValidation.numbers') }}</span>
        </div>
        <div class="flex items-center" :class="{ 'text-green-600': passwordValidationOneLetter }">
          <SfIconCheck v-if="passwordValidationOneLetter" size="sm" />
          <SfIconClose v-else size="sm" />
          <span class="ml-1">{{ t('authentication.signup.passwordValidation.letters') }}</span>
        </div>
      </div>

      <div class="flex items-center">
        <SfCheckbox
          id="privacyPolicy"
          v-model="privacyPolicy"
          v-bind="privacyPolicyAttributes"
          value="value"
          class="peer"
        />
        <label
          class="ml-3 text-base text-neutral-900 cursor-pointer peer-disabled:text-disabled-900 select-none"
          for="privacyPolicy"
        >
          <i18n-t keypath="form.privacyPolicyLabel" scope="global">
            <template #privacyPolicy>
              <UiLink
                :href="localePath(paths.privacyPolicy)"
                target="_blank"
                class="focus:outline focus:outline-offset-2 focus:outline-2 outline-secondary-600 rounded"
              >
                {{ t('legal.privacyPolicy') }}
              </UiLink>
            </template>
          </i18n-t>
          {{ t('form.required') }}
        </label>
      </div>
      <ErrorMessage as="div" name="register.privacyPolicy" class="text-negative-700 text-left text-sm" />

      <NuxtTurnstile
        v-if="turnstileSiteKey.length > 0 && turnstileLoad"
        v-bind="turnstileAttributes"
        ref="turnstileElement"
        v-model="turnstile"
        :site-key="turnstileSiteKey"
        :options="{ theme: 'light' }"
        class="mt-4 flex justify-center"
      />
      <ErrorMessage as="div" name="register.turnstile" class="text-negative-700 text-center text-sm" />

      <UiButton type="submit" class="mt-2" :disabled="loading || migrateLoading">
        <SfLoaderCircular v-if="loading || migrateLoading" class="flex justify-center items-center" size="base" />
        <span v-else>
          {{ t('authentication.signup.submitLabel') }}
        </span>
      </UiButton>

      <div v-if="changeableView" class="text-center">
        <div class="my-5 font-bold">{{ t('authentication.signup.alreadyHaveAccount') }}</div>
        <UiLink variant="primary" class="cursor-pointer" @click="$emit('change-view')">
          {{ t('authentication.signup.logInLinkLabel') }}
        </UiLink>
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import {
  SfInput,
  SfLoaderCircular,
  SfCheckbox,
  SfIconPerson,
  SfIconLocalShipping,
  SfIconFavorite,
  SfIconSchedule,
  SfIconCheck,
  SfIconClose,
} from '@storefront-ui/vue';
import { useForm, ErrorMessage } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import { object, string, boolean } from 'yup';
import type { RegisterFormParams } from '~/components/Register/types';
import { useMigrateGuestOrder } from '~/composables/useMigrateGuestOrder';
import { userGetters } from '@plentymarkets/shop-api';

const localePath = useLocalePath();
const router = useRouter();
const { register, loading, isAuthorized } = useCustomer();
const { send } = useNotification();
const { migrateGuestOrder, loading: migrateLoading } = useMigrateGuestOrder();
const viewport = useViewport();
const { getSetting } = useSiteSettings('cloudflareTurnstileApiSiteKey');
const runtimeConfig = useRuntimeConfig();

const emits = defineEmits(['registered', 'change-view']);
const { emailAddress, order, isModal = false, changeableView = true } = defineProps<RegisterFormParams>();

const turnstileSiteKey = getSetting() ?? '';
const passwordMinLength = runtimeConfig.public.passwordMinLength;
const passwordMaxLength = runtimeConfig.public.passwordMaxLength;

const turnstileElement = ref();
const turnstileLoad = ref(false);

const validationSchema = toTypedSchema(
  object({
    register: object({
      email: string()
        .trim()
        .required(t('error.email.required'))
        .test('is-valid-email', t('error.email.valid'), (mail: string) => userGetters.isValidEmailAddress(mail))
        .default(''),
      password: string()
        .required(t('error.password.required'))
        .transform((value) => (value ? value.replace(/\s/g, '') : value))
        .min(passwordMinLength, t('error.password.minLength', { min: passwordMinLength }))
        .max(passwordMaxLength, t('error.password.maxLength', { max: passwordMaxLength }))
        .matches(/^(?=.*[A-Za-z])(?=.*\d)/, t('error.password.valid'))
        .default(''),
      repeatPassword: string()
        .required(t('error.password.required'))
        .transform((value) => (value ? value.replace(/\s/g, '') : value))
        .test('passwords-match', t('error.password.match'), function (value) {
          const passwordValue = this.parent.password?.replace(/\s/g, '');
          return value === passwordValue;
        })
        .default(''),
      privacyPolicy: boolean().isTrue(t('legal.privacyPolicyRequired')).required(t('legal.privacyPolicyRequired')),
      turnstile:
        turnstileSiteKey.length > 0
          ? string().required(t('error.turnstileRequired')).default('')
          : string().optional().default(''),
    }),
  }),
);

const { errors, meta, defineField, handleSubmit } = useForm({ validationSchema: validationSchema });
const [email, emailAttributes] = defineField('register.email');
const [password, passwordAttributes] = defineField('register.password');
const [repeatPassword, repeatPasswordAttributes] = defineField('register.repeatPassword');
const [turnstile, turnstileAttributes] = defineField('register.turnstile');
const [privacyPolicy, privacyPolicyAttributes] = defineField('register.privacyPolicy');

if (emailAddress) email.value = emailAddress;

const stripSpaces = (fieldName: 'password' | 'repeatPassword') => {
  const fieldRef = fieldName === 'password' ? password : repeatPassword;
  const currentValue = fieldRef.value;

  if (currentValue && typeof currentValue === 'string') {
    fieldRef.value = currentValue.replace(/\s/g, '');
  }
};

const clearTurnstile = () => {
  turnstile.value = '';
  turnstileElement.value?.reset();
};

const registerUser = async () => {
  if (!meta.value.valid || (!turnstile.value && turnstileSiteKey.length > 0) || isAuthorized.value) {
    return;
  }

  const response = await register({
    'cf-turnstile-response': turnstile.value,
    contact: {
      password: password.value ?? '',
      typeId: 1,
      referrerId: 1,
      options: {
        typeId: {
          value: email.value ?? '',
          subTypeId: 4,
          priority: 0,
          typeId: 2,
        },
      },
    },
  });

  if (response?.data.code === 1) {
    send({ message: t('authentication.signup.emailAlreadyExists'), type: 'negative' });
    clearTurnstile();
    return;
  }

  if (response?.data.id) {
    send({ message: t('authentication.signup.success'), type: 'positive' });

    if (order) {
      await migrateGuestOrder({
        orderId: order?.order.id ?? -1,
        accessKey: order?.order.accessKey ?? '',
        postcode: order?.order.deliveryAddress.postalCode ?? undefined,
        name: order?.order.deliveryAddress.name3 ?? undefined,
      });
    }

    emits('registered');
    clearTurnstile();

    if (!order) viewport.isGreaterOrEquals('lg') ? router.push(router.currentRoute.value.path) : router.back();
  }
  clearTurnstile();
};

const onSubmit = handleSubmit(() => registerUser());

const passwordValidationLength = computed(() => {
  const val = password?.value || '';
  return val.length >= passwordMinLength && val.length <= passwordMaxLength;
});

const passwordValidationOneDigit = computed(() => /\d/.test(password?.value || ''));
const passwordValidationOneLetter = computed(() => /[A-Za-z]/.test(password?.value || ''));

if (turnstileSiteKey.length > 0) {
  const turnstileWatcher = watch([email, password, repeatPassword], (data) => {
    if (data.some((field) => field && field.length > 0)) {
      turnstileLoad.value = true;
      turnstileWatcher();
    }
  });
}
</script>
